/*
  Ship-by countdown: "Ships today if you order within the next 3h 42m."

  Mounted by snippets/shipping-countdown.liquid, which is the one place the
  business rules live — cutoff time, timezone, closed weekdays and any extra
  closed dates all arrive as data attributes.

  All date math runs in the shop's timezone (data-timezone), never the
  visitor's, so a customer in Berlin sees the same cutoff as one in Austin.
  Intl.DateTimeFormat does the conversion, which keeps DST correct without a
  timezone library.
*/

const SHIPPING_COUNTDOWN_DAY_MS = 86400000;

// Difference between a timezone's wall clock and UTC at a given instant.
function zoneOffsetMs(epochMs, timeZone) {
  const parts = {};
  for (const part of shippingCountdownFormatter(timeZone).formatToParts(epochMs)) {
    parts[part.type] = part.value;
  }
  // en-US with hour12:false reports midnight as hour 24, hence the modulo.
  const asIfUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  return asIfUTC - epochMs + (epochMs % 1000);
}

const shippingCountdownFormatters = new Map();

function shippingCountdownFormatter(timeZone) {
  let formatter = shippingCountdownFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    shippingCountdownFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/*
  Calendar days are carried as a Date pinned to UTC midnight — a bare Y/M/D
  marker with no time-of-day meaning. That makes "add a day" and "compare two
  days" plain arithmetic, with no local-timezone surprises.
*/
function zonedCalendarDay(epochMs, timeZone) {
  const parts = {};
  for (const part of shippingCountdownFormatter(timeZone).formatToParts(epochMs)) {
    parts[part.type] = part.value;
  }
  return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
}

// The instant at which a wall-clock time on a calendar day falls in timeZone.
function wallClockToEpoch(day, hour, minute, timeZone) {
  const naive = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), hour, minute, 0);
  // Two passes so a cutoff sitting near a DST transition resolves correctly.
  const firstPass = naive - zoneOffsetMs(naive, timeZone);
  return naive - zoneOffsetMs(firstPass, timeZone);
}

function isoDay(day) {
  return day.toISOString().slice(0, 10);
}

/*
  The visitor's own calendar day, in whatever zone their device is set to.
  "today" and "tomorrow" are read against this, not against the shop's
  calendar — see shipDayLabel.
*/
function visitorCalendarDay(epochMs) {
  const local = new Date(epochMs);
  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
}

function nthWeekdayOfMonth(year, month, weekday, n) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const shift = (weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month - 1, 1 + shift + (n - 1) * 7));
}

function lastWeekdayOfMonth(year, month, weekday) {
  // Day 0 of the following month is the last day of this one.
  const last = new Date(Date.UTC(year, month, 0));
  const shift = (last.getUTCDay() - weekday + 7) % 7;
  return new Date(Date.UTC(year, month - 1, last.getUTCDate() - shift));
}

/*
  A fixed-date holiday landing on a Sunday is observed the Monday after. One
  landing on a Saturday is left alone: USPS simply doesn't deliver that
  Saturday rather than closing the Friday before.
*/
function observedHoliday(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCDay() === 0) date.setUTCDate(date.getUTCDate() + 1);
  return date;
}

const uspsHolidayCache = new Map();

// The eleven federal holidays USPS observes, computed rather than hardcoded so
// this needs no yearly maintenance.
function uspsHolidays(year) {
  let holidays = uspsHolidayCache.get(year);
  if (holidays) return holidays;

  const dates = [
    observedHoliday(year, 1, 1), // New Year's Day
    observedHoliday(year, 6, 19), // Juneteenth
    observedHoliday(year, 7, 4), // Independence Day
    observedHoliday(year, 11, 11), // Veterans Day
    observedHoliday(year, 12, 25), // Christmas Day
    nthWeekdayOfMonth(year, 1, 1, 3), // MLK Jr. Day
    nthWeekdayOfMonth(year, 2, 1, 3), // Washington's Birthday
    lastWeekdayOfMonth(year, 5, 1), // Memorial Day
    nthWeekdayOfMonth(year, 9, 1, 1), // Labor Day
    nthWeekdayOfMonth(year, 10, 1, 2), // Columbus Day
    nthWeekdayOfMonth(year, 11, 4, 4), // Thanksgiving
  ];

  holidays = new Set(dates.map(isoDay));
  uspsHolidayCache.set(year, holidays);
  return holidays;
}

function formatRemaining(ms) {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;

  // Under an hour the seconds are the whole point of the nudge.
  const seconds = Math.floor(ms / 1000) % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function formatRemainingLong(ms) {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  return parts.join(' ');
}

function isoDuration(ms) {
  const totalMinutes = Math.floor(ms / 60000);
  return `PT${Math.floor(totalMinutes / 60)}H${totalMinutes % 60}M`;
}

/*
  One ticker drives every mounted countdown. Several can be on the page at once
  — the cart page plus the add-to-cart notification — and a shared interval
  keeps them showing the same number.
*/
const shippingCountdownInstances = new Set();
let shippingCountdownTimer = null;

function startShippingCountdownTicker() {
  if (shippingCountdownTimer) return;
  shippingCountdownTimer = setInterval(() => {
    shippingCountdownInstances.forEach((instance) => instance.refresh());
  }, 1000);
}

function stopShippingCountdownTicker() {
  if (!shippingCountdownTimer || shippingCountdownInstances.size > 0) return;
  clearInterval(shippingCountdownTimer);
  shippingCountdownTimer = null;
}

class ShippingCountdown extends HTMLElement {
  connectedCallback() {
    if (!this.config) {
      this.config = this.readConfig();
      this.clockSkewMs = this.readClockSkew();
      this.buildMarkup();
    }

    this.refresh();
    shippingCountdownInstances.add(this);
    startShippingCountdownTicker();
  }

  disconnectedCallback() {
    shippingCountdownInstances.delete(this);
    stopShippingCountdownTicker();
  }

  readConfig() {
    const [hour, minute] = (this.dataset.cutoff || '14:00').split(':');
    return {
      timeZone: this.dataset.timezone || 'America/Chicago',
      cutoffHour: Number(hour),
      cutoffMinute: Number(minute || 0),
      closedWeekdays: (this.dataset.closedWeekdays || '')
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => !Number.isNaN(value)),
      closedDates: new Set(
        (this.dataset.closedDates || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      ),
      observeHolidays: this.dataset.observeHolidays !== 'false',
      showInfo: this.dataset.showInfo === 'true',
    };
  }

  /*
    Trust the visitor's clock for the tick, but not for the hour it claims to
    be. A clock set wrong by hours would advertise the wrong ship day, so fall
    back to the server timestamp when the two disagree by more than fifteen
    minutes. Small disagreements are ignored: a cached page's timestamp can be
    a few minutes stale and the visitor's clock is the better reference there.
  */
  readClockSkew() {
    const serverSeconds = Number(this.dataset.now);
    if (!serverSeconds) return 0;
    const skew = serverSeconds * 1000 - Date.now();
    return Math.abs(skew) > 15 * 60 * 1000 ? skew : 0;
  }

  now() {
    return Date.now() + this.clockSkewMs;
  }

  isShippingDay(day) {
    if (this.config.closedWeekdays.includes(day.getUTCDay())) return false;
    if (this.config.closedDates.has(isoDay(day))) return false;
    if (this.config.observeHolidays && uspsHolidays(day.getUTCFullYear()).has(isoDay(day))) return false;
    return true;
  }

  /*
    The next cutoff is the first moment still in the future at which orders
    stop shipping that day — which is exactly the day the order will ship. Past
    the cutoff it rolls to the next open day, so 3pm Monday correctly reads
    "ships tomorrow" with the Tuesday 2pm deadline.
  */
  nextShipment(nowMs) {
    const today = zonedCalendarDay(nowMs, this.config.timeZone);

    for (let offset = 0; offset < 30; offset += 1) {
      const day = new Date(today.getTime() + offset * SHIPPING_COUNTDOWN_DAY_MS);
      if (!this.isShippingDay(day)) continue;

      const cutoffMs = wallClockToEpoch(day, this.config.cutoffHour, this.config.cutoffMinute, this.config.timeZone);
      if (cutoffMs > nowMs) return { cutoffMs, day };
    }

    return null;
  }

  /*
    The ship date is a date on the shop's calendar, but "today" and "tomorrow"
    are read against the visitor's, so the gap is measured between the two.
    Anchoring to the shop's calendar instead would mislead anyone whose local
    date differs: at 7pm Monday in Austin a Berlin visitor is already on
    Tuesday, so shop-relative "tomorrow" reads to them as Wednesday. It bites
    domestically too — just after midnight in Austin it is still yesterday on
    the west coast.

    Anything that isn't today or tomorrow gets a weekday name. A negative gap
    means the visitor's date has already rolled past the ship date (far-east
    timezones), where no relative word is truthful, so the date is spelled out
    alongside the weekday — as it is for anything more than a week away, where
    a bare weekday would be ambiguous.
  */
  shipDayLabel(shipment, nowMs) {
    const gapDays = Math.round((shipment.day - visitorCalendarDay(nowMs)) / SHIPPING_COUNTDOWN_DAY_MS);

    if (gapDays === 0) return 'today';
    if (gapDays === 1) return 'tomorrow';

    const options = { timeZone: 'UTC', weekday: 'long' };
    if (gapDays < 0 || gapDays > 6) {
      options.month = 'short';
      options.day = 'numeric';
    }
    return new Intl.DateTimeFormat('en-US', options).format(shipment.day);
  }

  buildMarkup() {
    this.innerHTML = `
      <span class="shipping-countdown__headline">
        <span class="shipping-countdown__icon" aria-hidden="true">
          <i class="fa-regular fa-truck-fast"></i>
        </span>
        Ships <strong class="shipping-countdown__day"></strong> if you order within the next
        <time class="shipping-countdown__clock">
          <span class="shipping-countdown__clock-short" aria-hidden="true"></span>
          <span class="visually-hidden"></span>
        </time>
        ${
          this.config.showInfo
            ? `<span class="shipping-countdown__info">
                <button type="button" class="shipping-countdown__info-button" aria-expanded="false">
                  <span class="visually-hidden">Shipping cutoff details</span>
                  <i class="fa-regular fa-circle-info" aria-hidden="true"></i>
                </button>
                <span class="shipping-countdown__info-bubble" role="note" hidden></span>
              </span>`
            : ''
        }
      </span>
    `;

    this.clockTarget = this.querySelector('.shipping-countdown__clock');
    this.clockShortTarget = this.querySelector('.shipping-countdown__clock-short');
    this.clockSpokenTarget = this.querySelector('.shipping-countdown__clock .visually-hidden');
    this.dayTarget = this.querySelector('.shipping-countdown__day');
    this.infoBubbleTarget = this.querySelector('.shipping-countdown__info-bubble');
    if (this.infoBubbleTarget) this.bindInfoToggle();
  }

  /*
    Click-to-toggle disclosure, deliberately not hover-to-reveal. Hover has to
    be paired with click anyway for touch, and the two fight: the button takes
    focus on mousedown, so a click on an already-hovered button reads as a
    second toggle and shuts the bubble again. Dawn's quantity-popover carries a
    flag to paper over exactly that. One trigger avoids the whole problem and
    behaves the same for mouse, touch and keyboard.
  */
  bindInfoToggle() {
    this.infoButton = this.querySelector('.shipping-countdown__info-button');

    this.infoButton.addEventListener('click', (event) => {
      event.preventDefault();
      // Keep the click off document, or the handler below would see it as an
      // outside click and close the bubble the moment it opened.
      event.stopPropagation();
      this.toggleInfo(this.infoButton.getAttribute('aria-expanded') !== 'true');
    });

    this.closeInfoOnOutsideClick = (event) => {
      if (!this.contains(event.target)) this.toggleInfo(false);
    };

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.toggleInfo(false);
      this.infoButton.focus();
    });

    // Tabbing away closes it. The timeout lets focus land before we ask where.
    this.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.toggleInfo(false);
      });
    });
  }

  toggleInfo(open) {
    this.infoButton.setAttribute('aria-expanded', String(open));
    this.infoBubbleTarget.toggleAttribute('hidden', !open);

    if (open) {
      document.addEventListener('click', this.closeInfoOnOutsideClick);
    } else {
      document.removeEventListener('click', this.closeInfoOnOutsideClick);
    }
  }

  refresh() {
    const nowMs = this.now();
    const shipment = this.nextShipment(nowMs);

    // Nothing open in the next month means the config is wrong (every weekday
    // closed, say). Stay hidden rather than promise a date.
    if (!shipment) {
      this.hidden = true;
      return;
    }

    const remainingMs = shipment.cutoffMs - nowMs;
    const dayLabel = this.shipDayLabel(shipment, nowMs);

    // "3h 42m" reads as "three h forty-two m" aloud, so the compact form is
    // hidden from assistive tech and a spelled-out twin sits beside it. Neither
    // is in a live region: a value that changes every second must not announce.
    this.clockShortTarget.textContent = formatRemaining(remainingMs);
    this.clockSpokenTarget.textContent = formatRemainingLong(remainingMs);
    this.clockTarget.setAttribute('datetime', isoDuration(remainingMs));
    this.dayTarget.textContent = dayLabel;

    if (this.infoBubbleTarget) {
      const cutoffLabel = new Intl.DateTimeFormat('en-US', {
        timeZone: this.config.timeZone,
        hour: 'numeric',
        minute: this.config.cutoffMinute ? '2-digit' : undefined,
        timeZoneName: 'short',
      }).format(shipment.cutoffMs);
      this.infoBubbleTarget.textContent = `Orders placed before ${cutoffLabel} ship the same day, excluding Sundays and holidays.`;
    }

    this.classList.toggle('shipping-countdown--urgent', remainingMs < 60 * 60 * 1000);
    this.hidden = false;
  }
}

customElements.define('shipping-countdown', ShippingCountdown);
